import React, { useState, useEffect, useCallback, useRef } from 'react';
import useGameStore from '../../store/gameStore';
import { ADVISORS, getAdvisorMoodImage, getEventDialogue, getSituationalReaction, getLocalizedAdvisor, getAdvisorMoodLabel } from '../../data/advisors';
import { X, MessageCircle, ChevronDown } from 'lucide-react';

// Determine situational trigger from game state
const determineSituationalTrigger = (state, prevState) => {
  if (!state || !state.gameStarted) return null;

  const { totalDays, treasury, reputation, drivers, vehicles, activeContracts, phase, lastDayRevenue, lastDayExpenses, day } = state;

  // First day
  if (totalDays === 0) return 'first_day';

  // Milestones
  if (totalDays === 30 && (!prevState || prevState.totalDays < 30)) return 'milestone_30days';
  if (totalDays === 180 && (!prevState || prevState.totalDays < 180)) return 'milestone_180days';

  // Phase up
  if (prevState && phase > prevState.phase) return 'phase_up';

  // First contract
  if (prevState && activeContracts.length > 0 && (!prevState.activeContracts || prevState.activeContracts.length === 0)) {
    return 'first_contract';
  }

  // Treasury warnings
  if (treasury < 500) return 'treasury_critical';
  if (treasury < 2000 && (!prevState || prevState.treasury >= 2000)) return 'treasury_low';

  // Good/bad day
  const dailyResult = lastDayRevenue - lastDayExpenses;
  if (dailyResult > 500 && totalDays > 1) return 'good_profit';
  if (dailyResult < -300 && totalDays > 3) return 'bad_result';

  // Reputation
  if (reputation > 80 && prevState && prevState.reputation <= 80) return 'reputation_high';
  if (reputation < 30 && prevState && prevState.reputation >= 30) return 'reputation_low';

  // Weekend
  if (day > 5 && day <= 7) return 'weekend';

  // No drivers after day 3
  if (totalDays > 3 && drivers.length === 0) return 'no_drivers';

  // Fleet idle
  const idleVehicles = vehicles.filter(v => v.status === 'idle' && !v.assignedContractId);
  if (idleVehicles.length > 1 && totalDays > 7 && Math.random() < 0.3) return 'fleet_idle';

  return null;
};

export const AdvisorBubble = () => {
  const advisorId = useGameStore(s => s.selectedAdvisor);
  const advisorEnabled = useGameStore(s => s.advisorEnabled);
  const pendingEvents = useGameStore(s => s.pendingEvents);
  const totalDays = useGameStore(s => s.totalDays);
  const treasury = useGameStore(s => s.treasury);
  const reputation = useGameStore(s => s.reputation);
  const phase = useGameStore(s => s.phase);
  const drivers = useGameStore(s => s.drivers);
  const vehicles = useGameStore(s => s.vehicles);
  const activeContracts = useGameStore(s => s.activeContracts);
  const lastDayRevenue = useGameStore(s => s.lastDayRevenue);
  const lastDayExpenses = useGameStore(s => s.lastDayExpenses);
  const day = useGameStore(s => s.day);
  const gameStarted = useGameStore(s => s.gameStarted);
  // 28/05/2026 — Mute the advisor bubble entirely while the onboarding tour
  // is running. The two UIs were stacking on top of each other (advisor
  // bubble in the same bottom-right corner as the tutorial card), creating
  // a confusing experience for new players. The advisor resumes the moment
  // the onboarding tour is skipped or finished.
  const tutorialComplete = useGameStore(s => s.tutorialComplete);
  const onboardingActive = !tutorialComplete;

  const [visible, setVisible] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [currentDialogue, setCurrentDialogue] = useState(null);
  const [currentMood, setCurrentMood] = useState(null);
  const [prevState, setPrevState] = useState(null);
  const [lastTriggerDay, setLastTriggerDay] = useState(-1);

  const advisor = advisorId ? getLocalizedAdvisor(advisorId) : null;

  // Check for event-based reactions
  useEffect(() => {
    if (!advisor || !advisorEnabled || !gameStarted) return;
    if (pendingEvents.length > 0) {
      const event = pendingEvents[0];
      const reaction = getEventDialogue(advisorId, event.category);
      if (reaction) {
        setCurrentDialogue(reaction.dialogue);
        setCurrentMood(reaction.mood);
        setVisible(true);
        setMinimized(false);
      }
    }
  }, [pendingEvents, advisor, advisorId, advisorEnabled, gameStarted]);

  // 07/02/2026 — code review: ce useEffect avait 15 dépendances et un
  // eslint-disable. Refactor : on stocke la snapshot d'état dans un useRef
  // et on n'écoute que les vrais déclencheurs (changement de jour ou
  // toggle conseiller). Les autres valeurs sont lues à la volée via la
  // ref, donc plus de stale closure ni de re-run excessif.
  const stateSnapshotRef = useRef({ totalDays, treasury, reputation, drivers, vehicles, activeContracts, phase, lastDayRevenue, lastDayExpenses, day, gameStarted });
  // Toujours mettre à jour la ref à chaque render (pas dans un effet pour
  // garantir la fraîcheur du tick suivant).
  stateSnapshotRef.current = { totalDays, treasury, reputation, drivers, vehicles, activeContracts, phase, lastDayRevenue, lastDayExpenses, day, gameStarted };

  useEffect(() => {
    if (!advisor || !advisorEnabled || !gameStarted) return;
    if (pendingEvents.length > 0) return;

    const currentState = stateSnapshotRef.current;

    if (totalDays === lastTriggerDay && totalDays > 0) {
      setPrevState(currentState);
      return;
    }

    const trigger = determineSituationalTrigger(currentState, prevState);
    if (trigger) {
      const reaction = getSituationalReaction(advisorId, trigger);
      if (reaction) {
        setCurrentDialogue(reaction.dialogue);
        setCurrentMood(reaction.mood);
        setVisible(true);
        setMinimized(false);
        setLastTriggerDay(totalDays);
      }
    }

    setPrevState(currentState);
    // prevState et lastTriggerDay sont des set-states stables — pas dans deps.
  }, [totalDays, advisor, advisorId, advisorEnabled, gameStarted, pendingEvents.length]);

  // Auto-hide after 12 seconds if no event popup
  useEffect(() => {
    if (visible && pendingEvents.length === 0 && !minimized) {
      const timer = setTimeout(() => setMinimized(true), 12000);
      return () => clearTimeout(timer);
    }
  }, [visible, pendingEvents.length, minimized, currentDialogue]);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    setCurrentDialogue(null);
  }, []);

  const handleMinimize = useCallback(() => {
    setMinimized(true);
  }, []);

  const handleExpand = useCallback(() => {
    setMinimized(false);
  }, []);

  if (!advisor || !advisorEnabled || !visible || !currentDialogue || onboardingActive) return null;

  const moodImage = currentMood ? getAdvisorMoodImage(advisorId, currentMood) : advisor.portrait;
  const moodLabel = currentMood ? getAdvisorMoodLabel(advisorId, currentMood) : '';

  // Minimized: just show the avatar as a clickable circle
  if (minimized) {
    return (
      <button
        onClick={handleExpand}
        className="fixed bottom-20 right-4 md:bottom-16 md:right-6 z-[90] group"
        data-testid="advisor-bubble-minimized"
      >
        <div className={`w-14 h-14 rounded-full overflow-hidden border-3 shadow-lg transition-transform group-hover:scale-110 ${advisor.borderColor}`}
          style={{ borderWidth: '3px', borderColor: advisor.accentColor }}
        >
          <img src={moodImage} alt={advisor.shortName} className="w-full h-full object-cover object-top" />
        </div>
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
          <MessageCircle className="w-2.5 h-2.5 text-white" />
        </div>
      </button>
    );
  }

  return (
    <div
      className={`fixed bottom-20 right-4 md:bottom-16 md:right-6 z-[90] w-80 max-w-[calc(100vw-2rem)] animate-in slide-in-from-bottom-4 duration-300`}
      data-testid="advisor-bubble"
    >
      <div
        className="surface-glass shadow-[0_8px_32px_rgba(0,0,0,0.6)] overflow-hidden border-2"
        style={{ borderColor: advisor.accentColor }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-3 py-2.5 border-b border-white/10" style={{ background: `linear-gradient(135deg, ${advisor.color} 0%, ${advisor.accentColor} 120%)` }}>
          <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/30 flex-shrink-0">
            <img src={moodImage} alt={advisor.shortName} className="w-full h-full object-cover object-top" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-white text-sm font-bold truncate">{advisor.shortName}</p>
            {moodLabel && (
              <p className="text-white/70 text-[10px] italic font-mono uppercase tracking-wider">{moodLabel}</p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleMinimize}
              aria-label="Réduire le conseiller"
              className="text-white/60 hover:text-white transition-colors p-1"
              data-testid="advisor-minimize-btn"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
            <button aria-label="Fermer"
              onClick={handleDismiss}
              className="text-white/60 hover:text-white transition-colors p-1"
              data-testid="advisor-dismiss-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Dialogue */}
        <div className="px-4 py-3">
          <p className="text-sm text-slate-200 leading-relaxed italic" data-testid="advisor-dialogue">
            &laquo;&nbsp;{currentDialogue}&nbsp;&raquo;
          </p>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('hst:open-insights'))}
            className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] font-semibold text-accent hover:text-foreground transition-colors"
            data-testid="advisor-ask-insights"
          >
            <MessageCircle className="w-3 h-3" />
            Demander un bilan stratégique
          </button>
        </div>
      </div>

      {/* Speech tail */}
      <div className="absolute -bottom-2 right-8 w-4 h-4 rotate-45 bg-ink-800/80 backdrop-blur-xl border-r-2 border-b-2"
        style={{ borderColor: advisor.accentColor }}
      />
    </div>
  );
};

export default AdvisorBubble;
